// Private editor: paste a LinkedIn post, let AI draft the card copy, review, save.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Sparkles, Trash2, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const ADMIN_TOKEN_KEY = "cta-clicks:admin-token:v1";
function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  let token = window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  if (!token) {
    token = window.prompt("Enter ADMIN_TOKEN to manage LinkedIn cards") ?? "";
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  }
  return token;
}
function forgetToken() {
  window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

type Row = {
  id?: string;
  linkedin_url: string;
  category: string;
  title: string;
  summary: string;
  post_date: string;
  image: string;
  image_alt: string | null;
  internal_url: string | null;
  published: boolean;
};

const EMPTY: Row = {
  linkedin_url: "",
  category: "",
  title: "",
  summary: "",
  post_date: "",
  image: "",
  image_alt: "",
  internal_url: "",
  published: false,
};

export const Route = createFileRoute("/admin/linkedin-cards")({
  head: () => ({
    meta: [
      { title: "LinkedIn Card Editor | Arif Khan" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "description", content: "Private editor for homepage LinkedIn cards." },
    ],
  }),
  component: Editor,
});

async function api(path: string, init?: RequestInit) {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", "x-admin-token": getAdminToken(), ...(init?.headers ?? {}) },
    cache: "no-store",
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401) forgetToken();
  if (!res.ok) throw new Error(body.error ?? `Request failed (${res.status})`);
  return body;
}

function Editor() {
  const [postText, setPostText] = useState("");
  const [form, setForm] = useState<Row>(EMPTY);
  const [cards, setCards] = useState<Row[]>([]);
  const [busy, setBusy] = useState<"ai" | "save" | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api("/api/admin/linkedin-cards");
      setCards(d.cards);
    } catch (e) {
      setMsg({ kind: "err", text: (e as Error).message });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const set = <K extends keyof Row>(k: K, v: Row[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function generate() {
    setBusy("ai");
    setMsg(null);
    try {
      const d = await api("/api/admin/linkedin-ai", { method: "POST", body: JSON.stringify({ text: postText }) });
      setForm((f) => ({ ...f, title: d.title, category: d.topic, summary: d.takeaway }));
      setMsg({ kind: "ok", text: "Draft ready — review and edit before saving." });
    } catch (e) {
      setMsg({ kind: "err", text: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    setBusy("save");
    setMsg(null);
    try {
      await api("/api/admin/linkedin-cards", { method: "POST", body: JSON.stringify(form) });
      setMsg({ kind: "ok", text: form.published ? "Saved and live on the homepage." : "Saved as a draft." });
      setForm(EMPTY);
      setPostText("");
      load();
    } catch (e) {
      setMsg({ kind: "err", text: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this card?")) return;
    try {
      await api(`/api/admin/linkedin-cards?id=${id}`, { method: "DELETE" });
      load();
    } catch (e) {
      setMsg({ kind: "err", text: (e as Error).message });
    }
  }

  const liveCount = cards.filter((c) => c.published).length;

  return (
    <main className="container-page py-12 space-y-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">LinkedIn card editor</h1>
        <p className="mt-2 text-sm text-ink-soft">
          Paste a post, let AI draft the title, topic and takeaway, then add the cover image and date. The homepage shows the 3 newest published cards.
        </p>
      </div>

      {msg && (
        <p role="status" className={`rounded-md border px-4 py-3 text-sm ${msg.kind === "err" ? "border-destructive text-destructive" : "border-border"}`}>
          {msg.text}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>1. Paste the LinkedIn post</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea rows={8} value={postText} onChange={(e) => setPostText(e.target.value)} placeholder="Paste the full post text here…" />
          <Button onClick={generate} disabled={busy !== null || postText.trim().length < 20}>
            <Sparkles className="h-4 w-4" /> {busy === "ai" ? "Drafting…" : "Draft card with AI"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2. Review and complete the card {form.id && "(editing)"}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Title" full><Input value={form.title} maxLength={120} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Topic"><Input value={form.category} maxLength={40} onChange={(e) => set("category", e.target.value)} /></Field>
          <Field label="Date posted"><Input type="date" value={form.post_date} onChange={(e) => set("post_date", e.target.value)} /></Field>
          <Field label="Takeaway" full><Textarea rows={3} value={form.summary} maxLength={300} onChange={(e) => set("summary", e.target.value)} /></Field>
          <Field label="LinkedIn post link" full><Input value={form.linkedin_url} placeholder="https://www.linkedin.com/feed/update/…" onChange={(e) => set("linkedin_url", e.target.value)} /></Field>
          <Field label="Cover image (site path or https link)"><Input value={form.image} placeholder="/images/linkedin/cover.jpg" onChange={(e) => set("image", e.target.value)} /></Field>
          <Field label="Image description (optional)"><Input value={form.image_alt ?? ""} onChange={(e) => set("image_alt", e.target.value)} /></Field>
          <Field label="Related page on this site (optional)"><Input value={form.internal_url ?? ""} placeholder="/ask-arif/…" onChange={(e) => set("internal_url", e.target.value)} /></Field>
          <div className="flex items-center gap-3 self-end pb-2">
            <Switch id="pub" checked={form.published} onCheckedChange={(v) => set("published", v)} />
            <Label htmlFor="pub">Show on homepage</Label>
          </div>
          <div className="md:col-span-2 flex gap-3">
            <Button onClick={save} disabled={busy !== null}>{busy === "save" ? "Saving…" : "Save card"}</Button>
            {form.id && <Button variant="outline" onClick={() => setForm(EMPTY)}>Cancel edit</Button>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Saved cards ({liveCount} live)</CardTitle>
        </CardHeader>
        <CardContent>
          {cards.length === 0 ? (
            <p className="text-sm text-ink-soft">No cards yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {cards.map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-4 py-4">
                  <div>
                    <p className="text-xs text-ink-soft">{c.post_date} · {c.category} · {c.published ? "Live" : "Draft"}</p>
                    <p className="font-medium">{c.title}</p>
                    <p className="text-sm text-ink-soft">{c.summary}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="icon" variant="outline" aria-label="Edit" onClick={() => { setForm(c); window.scrollTo({ top: 0, behavior: "smooth" }); }}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="outline" aria-label="Delete" onClick={() => remove(c.id!)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}
